import type {
	IntegrationAppCategory,
	IntegrationAppListingField,
} from "@repo/shared-primitives"
import { ChevronDown, LayoutList } from "lucide-react"
import * as React from "react"
import { DetailPanel } from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { StatusDot } from "@/components/ui/status"
import {
	gameLabel,
	languageLabel,
} from "@/features/integration-apps/app-audience"
import { INTEGRATION_APP_CATEGORY_LABELS } from "@/features/integration-apps/CategoryPicker"
import type { GetListingProposalResponse } from "@/types/marketplace.types"
import { cn } from "@/utils/styles.helpers"
import { LISTING_FIELD_LABELS } from "./listing-proposal-labels"

type ListingFieldDiff = GetListingProposalResponse["diff"][number]
type Screenshot = Extract<
	ListingFieldDiff,
	{ field: "screenshots" }
>["current"][number]

/** Locales shown first, in this order; any other locale follows alphabetically. */
const LOCALE_ORDER = ["en", "fr"]

function isHttpUrl(value: string): boolean {
	return /^https?:\/\//i.test(value)
}

function Empty({ children }: { children: React.ReactNode }) {
	return <span className="text-meta italic">{children}</span>
}

/** Inside an applied field, single-column values show the proposed side. */
const ProposedOnlyContext = React.createContext(false)

/** Current on the left, proposed on the right; a single column when nothing changed. */
function Sides({
	changed,
	current,
	proposed,
}: {
	changed: boolean
	current: React.ReactNode
	proposed: React.ReactNode
}) {
	const proposedOnly = React.useContext(ProposedOnlyContext)

	if (!changed) {
		return <div className="min-w-0">{proposedOnly ? proposed : current}</div>
	}

	return (
		<div className="grid gap-4 md:grid-cols-2">
			<div className="min-w-0 space-y-1.5">
				<div className="font-semibold text-[11px] text-meta uppercase tracking-[0.07em]">
					Current
				</div>
				{current}
			</div>
			<div className="min-w-0 space-y-1.5">
				<div className="font-semibold text-[11px] text-primary-text uppercase tracking-[0.07em]">
					Proposed
				</div>
				{proposed}
			</div>
		</div>
	)
}

function TextValue({ value }: { value: string | null }) {
	if (!value) {
		return <Empty>Not set</Empty>
	}
	return <p className="text-sm whitespace-pre-wrap break-words">{value}</p>
}

function UrlValue({ value }: { value: string | null }) {
	if (!value) {
		return <Empty>Not set</Empty>
	}
	if (!isHttpUrl(value)) {
		return <p className="text-sm break-all">{value}</p>
	}
	return (
		<a
			href={value}
			target="_blank"
			rel="noopener noreferrer"
			className="break-all text-primary-text underline underline-offset-2"
		>
			{value}
		</a>
	)
}

function LogoValue({ value }: { value: string | null }) {
	if (!value) {
		return <Empty>No logo</Empty>
	}
	return (
		<img
			src={value}
			alt="Logo"
			className="h-20 w-20 rounded-md border object-cover"
		/>
	)
}

function orderedLocales(
	current: Record<string, string>,
	proposed: Record<string, string>,
): string[] {
	const locales = new Set([...Object.keys(current), ...Object.keys(proposed)])
	return [...locales].sort((a, b) => {
		const rankA = LOCALE_ORDER.indexOf(a)
		const rankB = LOCALE_ORDER.indexOf(b)
		if (rankA !== -1 || rankB !== -1) {
			return (
				(rankA === -1 ? LOCALE_ORDER.length : rankA) -
				(rankB === -1 ? LOCALE_ORDER.length : rankB)
			)
		}
		return a.localeCompare(b)
	})
}

/** Per-locale text, current against proposed; text is shown as typed, never rendered as Markdown. */
function LocalizedDiff({
	current,
	proposed,
	changed,
	long,
}: {
	current: Record<string, string>
	proposed: Record<string, string>
	changed: boolean
	long?: boolean
}) {
	const locales = orderedLocales(current, proposed)
	if (locales.length === 0) {
		return <Empty>Not set in any language</Empty>
	}

	const textClass = cn(
		"text-sm whitespace-pre-wrap break-words",
		long && "max-h-96 overflow-y-auto rounded-md border bg-muted/30 p-3",
	)

	return (
		<div className="space-y-4">
			{locales.map((locale) => {
				const before = current[locale]?.trim() ?? ""
				const after = proposed[locale]?.trim() ?? ""
				const localeChanged = changed && before !== after

				return (
					<div key={locale} className="space-y-1.5">
						<div className="flex items-center gap-2 text-xs font-medium">
							<span>{languageLabel(locale)}</span>
							{changed && !localeChanged && (
								<span className="font-normal text-muted-foreground">
									unchanged
								</span>
							)}
						</div>
						<Sides
							changed={localeChanged}
							current={
								before ? (
									<p className={textClass}>{before}</p>
								) : (
									<Empty>Not set</Empty>
								)
							}
							proposed={
								after ? (
									<p className={textClass}>{after}</p>
								) : (
									<Empty>Removed</Empty>
								)
							}
						/>
					</div>
				)
			})}
		</div>
	)
}

type ChipMark = "added" | "removed" | null

function Chip({ label, mark }: { label: string; mark: ChipMark }) {
	return (
		<Badge
			variant="outline"
			className={cn(
				"font-normal",
				mark === "added" && "border-transparent bg-ok-soft text-ok",
				mark === "removed" &&
					"border-transparent bg-bad-soft text-bad line-through",
			)}
		>
			{mark === "added" && <span aria-hidden="true">+ </span>}
			{mark === "removed" && <span aria-hidden="true">− </span>}
			{label}
			{mark && <span className="sr-only"> ({mark})</span>}
		</Badge>
	)
}

/** Value sets as chips; removed values are struck through on the left, added ones marked on the right. */
function ChipsDiff<T extends string>({
	current,
	proposed,
	changed,
	label,
	emptyLabel,
}: {
	current: T[]
	proposed: T[]
	changed: boolean
	label: (value: T) => string
	emptyLabel: string
}) {
	function renderSide(values: T[], other: T[], mark: ChipMark) {
		if (values.length === 0) {
			return <Empty>{emptyLabel}</Empty>
		}
		return (
			<div className="flex flex-wrap gap-1.5">
				{values.map((value) => (
					<Chip
						key={value}
						label={label(value)}
						mark={changed && !other.includes(value) ? mark : null}
					/>
				))}
			</div>
		)
	}

	return (
		<Sides
			changed={changed}
			current={renderSide(current, proposed, "removed")}
			proposed={renderSide(proposed, current, "added")}
		/>
	)
}

type ScreenshotMark = "added" | "removed" | "caption" | null

const SCREENSHOT_MARK_LABELS: Record<Exclude<ScreenshotMark, null>, string> = {
	added: "Added",
	removed: "Removed",
	caption: "Caption changed",
}

function ScreenshotThumb({
	screenshot,
	mark,
}: {
	screenshot: Screenshot
	mark: ScreenshotMark
}) {
	const image = (
		<img
			src={screenshot.url}
			alt={screenshot.caption ?? "Screenshot"}
			className="h-28 w-full rounded object-cover"
		/>
	)

	return (
		<figure
			className={cn(
				"relative w-44 space-y-1 rounded-md border p-1.5",
				mark === "added" && "border-ok bg-ok-soft",
				mark === "removed" && "border-bad bg-bad-soft opacity-80",
				mark === "caption" && "border-warn bg-warn-soft",
			)}
		>
			{isHttpUrl(screenshot.url) ? (
				<a href={screenshot.url} target="_blank" rel="noopener noreferrer">
					{image}
				</a>
			) : (
				image
			)}
			<figcaption className="text-xs break-words">
				{screenshot.caption ? (
					screenshot.caption
				) : (
					<span className="italic text-muted-foreground">No caption</span>
				)}
			</figcaption>
			{mark && (
				<span
					className={cn(
						"absolute left-2 top-2 rounded px-1.5 py-0.5 text-[10px] font-medium",
						mark === "added" && "bg-ok-soft text-ok backdrop-blur",
						mark === "removed" && "bg-bad-soft text-bad backdrop-blur",
						mark === "caption" && "bg-warn-soft text-warn backdrop-blur",
					)}
				>
					{SCREENSHOT_MARK_LABELS[mark]}
				</span>
			)}
		</figure>
	)
}

/** Both galleries as captioned thumbnails, marking added, removed and re-captioned screenshots. */
function ScreenshotsDiff({
	current,
	proposed,
	changed,
}: {
	current: Screenshot[]
	proposed: Screenshot[]
	changed: boolean
}) {
	const currentByUrl = new Map(current.map((shot) => [shot.url, shot]))
	const proposedUrls = new Set(proposed.map((shot) => shot.url))

	const sameSet =
		current.length === proposed.length &&
		current.every((shot) => proposedUrls.has(shot.url))
	const reordered =
		changed &&
		sameSet &&
		current.some((shot, index) => proposed[index]?.url !== shot.url)

	function currentMark(shot: Screenshot): ScreenshotMark {
		return changed && !proposedUrls.has(shot.url) ? "removed" : null
	}

	function proposedMark(shot: Screenshot): ScreenshotMark {
		const before = currentByUrl.get(shot.url)
		if (!before) {
			return "added"
		}
		return (before.caption ?? "") !== (shot.caption ?? "") ? "caption" : null
	}

	function renderGallery(
		shots: Screenshot[],
		markOf: (shot: Screenshot) => ScreenshotMark,
	) {
		if (shots.length === 0) {
			return <Empty>No screenshots</Empty>
		}
		return (
			<div className="flex flex-wrap gap-2">
				{shots.map((shot, index) => (
					<ScreenshotThumb
						key={`${shot.url}-${index}`}
						screenshot={shot}
						mark={markOf(shot)}
					/>
				))}
			</div>
		)
	}

	return (
		<div className="space-y-2">
			{reordered && (
				<p className="text-xs text-muted-foreground">
					Same screenshots in a new order.
				</p>
			)}
			<Sides
				changed={changed}
				current={renderGallery(current, currentMark)}
				proposed={renderGallery(proposed, proposedMark)}
			/>
		</div>
	)
}

function categoryLabel(category: IntegrationAppCategory): string {
	return INTEGRATION_APP_CATEGORY_LABELS[category] ?? category
}

function FieldBody({
	diff,
	changed,
}: {
	diff: ListingFieldDiff
	changed: boolean
}) {
	switch (diff.field) {
		case "name":
		case "developerName":
			return (
				<Sides
					changed={changed}
					current={<TextValue value={diff.current} />}
					proposed={<TextValue value={diff.proposed} />}
				/>
			)
		case "websiteUrl":
		case "supportUrl":
		case "privacyPolicyUrl":
		case "termsUrl":
			return (
				<Sides
					changed={changed}
					current={<UrlValue value={diff.current} />}
					proposed={<UrlValue value={diff.proposed} />}
				/>
			)
		case "logoUrl":
			return (
				<Sides
					changed={changed}
					current={<LogoValue value={diff.current} />}
					proposed={<LogoValue value={diff.proposed} />}
				/>
			)
		case "shortDescription":
			return (
				<LocalizedDiff
					current={diff.current}
					proposed={diff.proposed}
					changed={changed}
				/>
			)
		case "longDescription":
			return (
				<LocalizedDiff
					current={diff.current}
					proposed={diff.proposed}
					changed={changed}
					long
				/>
			)
		case "screenshots":
			return (
				<ScreenshotsDiff
					current={diff.current}
					proposed={diff.proposed}
					changed={changed}
				/>
			)
		case "categories":
			return (
				<ChipsDiff
					current={diff.current}
					proposed={diff.proposed}
					changed={changed}
					label={categoryLabel}
					emptyLabel="No categories"
				/>
			)
		case "games":
			return (
				<ChipsDiff
					current={diff.current}
					proposed={diff.proposed}
					changed={changed}
					label={gameLabel}
					emptyLabel="All games"
				/>
			)
		case "languages":
			return (
				<ChipsDiff
					current={diff.current}
					proposed={diff.proposed}
					changed={changed}
					label={languageLabel}
					emptyLabel="All languages"
				/>
			)
		default: {
			const unhandled: never = diff
			return unhandled
		}
	}
}

type FieldTone = "changed" | "muted" | "plain"

function DiffField({
	diff,
	changed,
	tone,
	badge,
	control,
	note,
	proposedOnly = false,
}: {
	diff: ListingFieldDiff
	/** Whether to show current and proposed side by side. */
	changed: boolean
	tone: FieldTone
	badge?: React.ReactNode
	control?: React.ReactNode
	note?: React.ReactNode
	proposedOnly?: boolean
}) {
	return (
		<section
			className={cn(
				"flex flex-col gap-3 p-4",
				tone === "changed" && "shadow-[inset_2px_0_0_0_var(--marker)]",
				tone === "muted" && "opacity-60",
			)}
		>
			<div className="flex flex-wrap items-center justify-between gap-2">
				<div className="flex items-center gap-2">
					<h3 className="font-medium">{LISTING_FIELD_LABELS[diff.field]}</h3>
					{badge}
				</div>
				{control}
			</div>
			{note}
			<ProposedOnlyContext.Provider value={proposedOnly}>
				<FieldBody diff={diff} changed={changed} />
			</ProposedOnlyContext.Provider>
		</section>
	)
}

function ChangedBadge() {
	return <Badge variant="info">Changed</Badge>
}

function OutcomeBadge({ accepted }: { accepted: boolean }) {
	return (
		<StatusDot tone={accepted ? "ok" : "warn"} className="text-xs">
			{accepted ? "Accepted" : "Sent back"}
		</StatusDot>
	)
}

function UnchangedFields({ entries }: { entries: ListingFieldDiff[] }) {
	if (entries.length === 0) {
		return null
	}

	return (
		<Collapsible>
			<CollapsibleTrigger className="group flex w-full cursor-pointer items-center gap-2 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:bg-(--row-hover) hover:text-foreground">
				<ChevronDown className="h-4 w-4 transition-transform group-data-[state=closed]:-rotate-90" />
				Unchanged fields ({entries.length})
			</CollapsibleTrigger>
			<CollapsibleContent className="divide-y border-t">
				{entries.map((entry) => (
					<DiffField
						key={entry.field}
						diff={entry}
						changed={false}
						tone="plain"
					/>
				))}
			</CollapsibleContent>
		</Collapsible>
	)
}

/** How the diff is reviewed: a selection while pending, the per-field outcome once decided. */
export type ListingProposalDiffReview =
	| {
			mode: "select"
			selected: ReadonlySet<IntegrationAppListingField>
			onToggle: (field: IntegrationAppListingField, selected: boolean) => void
			onSelectAll: (selected: boolean) => void
	  }
	| {
			mode: "outcome"
			accepted: readonly IntegrationAppListingField[]
			refused: readonly IntegrationAppListingField[]
	  }
	| { mode: "plain" }

interface ListingProposalDiffProps {
	diff: ListingFieldDiff[]
	review: ListingProposalDiffReview
	/** Muted text under the panel title, explaining what the comparison shows. */
	description?: React.ReactNode
	className?: string
}

/** A muted line in place of the field rows. */
function EmptyRow({ children }: { children: React.ReactNode }) {
	return <p className="p-4 text-muted-foreground">{children}</p>
}

function SelectAllControls({
	review,
}: {
	review: Extract<ListingProposalDiffReview, { mode: "select" }>
}) {
	return (
		<div className="flex items-center gap-1">
			<span className="text-xs text-meta">Select</span>
			<Button
				variant="ghost"
				size="sm"
				className="h-7 px-2"
				onClick={() => review.onSelectAll(true)}
			>
				all
			</Button>
			<span className="text-xs text-meta">/</span>
			<Button
				variant="ghost"
				size="sm"
				className="h-7 px-2"
				onClick={() => review.onSelectAll(false)}
			>
				none
			</Button>
		</div>
	)
}

function SelectableChanges({
	changed,
	review,
}: {
	changed: ListingFieldDiff[]
	review: Extract<ListingProposalDiffReview, { mode: "select" }>
}) {
	return (
		<>
			{changed.map((entry) => {
				const selected = review.selected.has(entry.field)
				const checkboxId = `accept-field-${entry.field}`

				return (
					<DiffField
						key={entry.field}
						diff={entry}
						changed
						tone={selected ? "changed" : "muted"}
						badge={<ChangedBadge />}
						control={
							<div className="flex items-center gap-2">
								<Checkbox
									id={checkboxId}
									checked={selected}
									onCheckedChange={(checked) =>
										review.onToggle(entry.field, checked === true)
									}
								/>
								<label htmlFor={checkboxId} className="text-xs font-medium">
									Accept this change
								</label>
							</div>
						}
						note={
							selected ? null : (
								<p className="text-xs text-meta">
									Not selected: sent back to the owner, who keeps what they
									wrote in their form.
								</p>
							)
						}
					/>
				)
			})}
		</>
	)
}

function DecidedFields({
	decided,
	review,
}: {
	decided: ListingFieldDiff[]
	review: Extract<ListingProposalDiffReview, { mode: "outcome" }>
}) {
	if (decided.length === 0) {
		return (
			<EmptyRow>No field was changed when this proposal was decided.</EmptyRow>
		)
	}

	return (
		<>
			{decided.map((entry) => {
				const accepted = review.accepted.includes(entry.field)
				return accepted ? (
					<DiffField
						key={entry.field}
						diff={entry}
						changed={false}
						tone="plain"
						badge={<OutcomeBadge accepted />}
						note={<p className="text-xs text-meta">Applied to the listing:</p>}
						proposedOnly
					/>
				) : (
					<DiffField
						key={entry.field}
						diff={entry}
						changed
						tone="plain"
						badge={<OutcomeBadge accepted={false} />}
						note={
							<p className="text-xs text-meta">
								Not applied: the live value is on the left, what the owner
								proposed on the right.
							</p>
						}
					/>
				)
			})}
		</>
	)
}

/**
 * Field-by-field comparison of a proposal against the live listing, as one
 * flush detail panel with a row per field. While pending, each changed field
 * can be ticked for acceptance (the selected count and select all or none sit
 * in the header bar); once decided with a per-field outcome, accepted fields
 * show the applied value and fields sent back show the live value against the
 * proposal. Unchanged fields are collapsed in the last row.
 */
export function ListingProposalDiff({
	diff,
	review,
	description,
	className,
}: ListingProposalDiffProps) {
	if (review.mode === "outcome") {
		const decided = diff.filter(
			(entry) =>
				review.accepted.includes(entry.field) ||
				review.refused.includes(entry.field),
		)
		const others = diff.filter((entry) => !decided.includes(entry))

		return (
			<DetailPanel
				title="Listing"
				icon={LayoutList}
				meta={description}
				flush
				className={className}
			>
				<div className="divide-y">
					<DecidedFields decided={decided} review={review} />
					<UnchangedFields entries={others} />
				</div>
			</DetailPanel>
		)
	}

	const changed = diff.filter((entry) => entry.changed)
	const unchanged = diff.filter((entry) => !entry.changed)
	const selectable = review.mode === "select" && changed.length > 0
	const selectedCount =
		review.mode === "select"
			? changed.filter((entry) => review.selected.has(entry.field)).length
			: 0

	return (
		<DetailPanel
			title="Listing"
			icon={LayoutList}
			badges={
				selectable && (
					<span className="text-xs text-muted-foreground tabular-nums">
						{selectedCount} of {changed.length} changes selected
					</span>
				)
			}
			meta={description}
			actions={
				review.mode === "select" &&
				selectable && <SelectAllControls review={review} />
			}
			flush
			className={className}
		>
			<div className="divide-y">
				{changed.length === 0 ? (
					<EmptyRow>Every proposed value matches the live listing.</EmptyRow>
				) : review.mode === "select" ? (
					<SelectableChanges changed={changed} review={review} />
				) : (
					changed.map((entry) => (
						<DiffField
							key={entry.field}
							diff={entry}
							changed
							tone="changed"
							badge={<ChangedBadge />}
						/>
					))
				)}
				<UnchangedFields entries={unchanged} />
			</div>
		</DetailPanel>
	)
}
